/**
 * Responsibility: one holding as a picture.
 *
 * Always the raw card render. A graded copy is the same card in a frame of
 * our own, because there is no per-slab render to fetch and because the
 * card is what you are looking for on a shelf.
 *
 * **The frame is always there.** For a raw copy it is transparent, and that
 * is the whole trick: the artwork occupies exactly the same rectangle
 * whether or not the card is slabbed, so a wall of tiles has one image size
 * and one footprint. Reserving the frame only for graded cards shrank their
 * artwork and made the grid look uneven even though the boxes matched.
 *
 * **The grade breaks the frame.** It sits on the top-right of the frame
 * line, centred on it, with the page colour behind it - so the line appears
 * to stop, carry the number, and resume. It never crosses the artwork, and
 * because the frame is inset from the tile edge the badge has room to
 * straddle it without leaving the box.
 *
 * Hovering scales the card towards the viewer rather than sliding it up the
 * page. A translate pushed the tile out of its grid cell and the scroll
 * container clipped it; a scale grows it about its own centre, inside the
 * padding the grid reserves.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ShelfCard } from '../shelf/shelfCard';
import { CardThumb } from '../grading/CardThumb';
import { GRID_WIDTH, atWidth } from './cardImage';
import { baseName, shortPrinting } from './printing';
import { gradeSkin, sheenGradient } from './gradingColours';

/** Frame thickness. Always reserved, transparent when the card is raw. */
const FRAME = 3;

/** How far the frame sits inside the tile, leaving room for the badge. */
const INSET = 7;

/** Card proportions, so the tile is a fixed box before anything loads. */
const CARD_RATIO = 7 / 5;

type CollectionTileProps = {
  card: ShelfCard;
  width: number;
  onOpen?: () => void;
  /** An overlay in the top-left, for a per-tile action like remove. */
  corner?: ReactNode;
};

/** The number alone: the badge is small and the tooltip carries the rest. */
function shortGrade(label: string): string {
  const found = /(\d+(?:\.\d)?)/.exec(label);
  return found ? found[1] : label;
}

export function CollectionTile({ card, width, onOpen, corner }: CollectionTileProps) {
  const graded = card.isGraded;
  const grade = shortGrade(card.gradeLabel ?? '');
  const skin = gradeSkin(grade);

  // The artwork sits inside the frame, which is inset from the tile edge.
  // Computed rather than left to the layout so the image is the same size
  // for every card, graded or not.
  const artWidth = width - (INSET + FRAME) * 2;

  return (
    <Stack spacing={0.5} sx={{ width, minWidth: 0 }}>
      <Box
        onClick={() => onOpen?.()}
        sx={{
          position: 'relative',
          width,
          height: Math.round(width * CARD_RATIO),
          cursor: onOpen ? 'pointer' : 'default',
          transition: 'transform 140ms ease, filter 140ms ease',
          transformOrigin: 'center',
          '&:hover': onOpen
            ? {
                transform: 'scale(1.06)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.45))',
                zIndex: 3,
              }
            : undefined,
        }}
      >
        {/* Present for every card so the artwork inside is always the
            same size; invisible unless the card is graded. */}
        <Box
          sx={{
            position: 'absolute',
            inset: INSET,
            borderRadius: 1.25,
            padding: `${FRAME}px`,
            background: graded ? sheenGradient(skin) : 'transparent',
            lineHeight: 0,
          }}
        >
          <CardThumb
            src={atWidth(card.imageUrl, GRID_WIDTH)}
            alt={card.name}
            width={artWidth}
          />
        </Box>

        {graded ? (
          <Box
            sx={{
              position: 'absolute',
              // Centred on the frame's top line, towards the right, so
              // the line reads as broken rather than overlaid.
              top: INSET,
              right: INSET + 8,
              transform: 'translateY(-50%)',
              px: 0.6,
              borderRadius: 0.75,
              // The page colour behind it is what breaks the line.
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                lineHeight: 1.4,
                color: skin.edge,
                letterSpacing: 0.2,
              }}
            >
              {grade}
            </Typography>
          </Box>
        ) : null}

        {corner ? (
          <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 3 }}>{corner}</Box>
        ) : null}

        {card.quantity > 1 ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: INSET + 2,
              right: INSET + 2,
              px: 0.75,
              py: 0.1,
              bgcolor: 'rgba(0,0,0,0.72)',
              color: 'common.white',
              borderTopLeftRadius: 6,
              fontSize: '0.68rem',
              fontWeight: 700,
              zIndex: 2,
            }}
          >
            x{card.quantity}
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="caption" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
          {baseName(card.name)}
        </Typography>
        {shortPrinting(card.name) ? (
          // **The printing, said out loud rather than left in brackets.**
          // A reverse holo and its plain copy share a name, a number and
          // an illustration, and differ in price by multiples. A shelf
          // that shows both as "Medicham" cannot be priced and cannot be
          // searched by the person who owns one of them.
          <Box
            sx={{
              flexShrink: 0,
              px: 0.6,
              borderRadius: 0.5,
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1.6,
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
            }}
          >
            {shortPrinting(card.name)}
          </Box>
        ) : null}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {card.caption}
      </Typography>
    </Stack>
  );
}
